export type PositionTrendsOnboardingStep = {
  id: string;
  anchor: string;
  title: string;
  body: string;
  example?: string;
  bullets?: string[];
  sections?: Array<{ title: string; items: string[] }>;
  closing?: string;
  boldLine?: string;
};

export const buildPositionTrendsOnboardingSteps = (): PositionTrendsOnboardingStep[] => [
  {
    id: 'step-1',
    anchor: 'position-trends-title',
    title: 'What you’re looking at',
    body:
      'This chart shows net positioning by participant class over time. Net positioning = long contracts - short contracts.',
    example: 'Who is leaning which way - and is that leaning changing?',
    closing: 'This is context, not a signal.',
  },
  {
    id: 'step-2',
    anchor: 'position-trends-chart',
    title: 'Understand the lines',
    body: 'Each line represents a different participant group:',
    sections: [
      {
        title: 'Dealers (blue)',
        items: [
          'Typically market makers and hedgers.',
          'Their net position often reflects client flow, not conviction.',
        ],
      },
      {
        title: 'Asset Managers (green)',
        items: [
          'Institutional investors with longer horizons.',
          'Their positioning changes tend to be slower and more structural.',
        ],
      },
      {
        title: 'Leveraged Money (pink)',
        items: [
          'Hedge funds and speculators.',
          'More tactical, faster to flip, often involved in squeezes and momentum.',
        ],
      },
      {
        title: 'Open Interest (yellow)',
        items: [
          'Total number of open contracts.',
          'Rising OI = new participation, falling OI = position reduction.',
        ],
      },
    ],
  },
  {
    id: 'step-3',
    anchor: 'position-trends-summary',
    title: 'Read the Dynamic Trend Summary',
    body:
      'The Dynamic Trend Summary at the top translates the chart into a plain-language desk read.',
    bullets: [
      'Direction (up or down vs prior week)',
      'Magnitude (how large the change is)',
      'Share of Open Interest (how crowded that position is)',
    ],
    example:
      'Asset Managers increasing net longs while Leveraged Funds reduce exposure suggests rotation, not momentum chasing.',
  },
  {
    id: 'step-4',
    anchor: 'position-trends-regime',
    title: 'Identify the regime label',
    body: 'In the top right, you will see a regime badge (for example, Flow-Driven).',
    bullets: [
      'Flow-Driven: positioning changes without expanding open interest. Often short-term, fragile, and prone to reversals.',
      'Structural (when present): positioning shifts with expanding open interest. Indicates longer-lasting conviction.',
      'This label affects how much weight you should give the chart.',
    ],
  },
  {
    id: 'step-5',
    anchor: 'position-trends-chart',
    title: 'Look for alignment vs divergence',
    body: 'Ask these questions as you scan the chart:',
    bullets: [
      'Are Asset Managers and Leveraged Money aligned or opposing?',
      'Is open interest expanding with the move?',
      'Are Dealers moving sharply, suggesting client positioning shifts?',
      'Alignment + rising OI = structural build',
      'Divergence + flat OI = flow-driven move',
      'Extreme net positions = crowding risk',
    ],
  },
  {
    id: 'step-6',
    anchor: 'position-trends-title',
    title: 'What this chart is NOT for',
    body: 'Do not use this panel to:',
    bullets: ['Time entries', 'Predict exact price moves', 'Trade on its own'],
    closing: 'This chart defines macro posture, not execution.',
  },
  {
    id: 'step-7',
    anchor: 'position-trends-summary',
    title: 'How this feeds the rest of the app',
    body: 'The Position Trends panel feeds into:',
    bullets: ['Macro Regime', 'Trade Readiness', 'Alert filtering', 'Confidence weighting'],
    closing: 'Execution comes later - from price structure, not this chart.',
  },
  {
    id: 'step-8',
    anchor: 'position-trends-chart',
    title: 'Final takeaway',
    body: 'Use this panel to understand pressure, not to force trades.',
    boldLine:
      'Net positioning tells you who is leaning and how crowded the room is. Price tells you when the room starts moving.',
  },
];
